import { Button } from '@/components/Button';

export interface KeypadProps {
  readonly onDigit: (digit: string) => void;
  readonly onBackspace: () => void;
}

/**
 * The on-screen keypad [Figma · `Keypad`, frames 01–04].
 *
 * Four rows of three, 12px apart, each key the design system's Button in its `Size=large, Style=Outline`
 * variant (component set `1818:1443`) — which is what `Button variant="outline"` already draws, down to the
 * 1.5px brand ring, the 12px radius and the 16/20 semibold label. The fourth row is a spacer, `0` and `⌫`.
 *
 * ⛔ **It is not removed when a keyboard is attached** [AC-3]. A Chromebook in tablet mode has no keyboard
 * out, and typing is handled by the screen rather than by this component: both paths reach the same digits.
 */
export function Keypad({ onDigit, onBackspace }: KeypadProps) {
  return (
    <div className="flex w-full flex-col gap-3">
      {[
        ['1', '2', '3'],
        ['4', '5', '6'],
        ['7', '8', '9'],
      ].map((row) => (
        <div className="grid w-full grid-cols-3 gap-3" key={row.join('')}>
          {row.map((digit) => (
            <Button fullWidth key={digit} onClick={() => onDigit(digit)} variant="outline">
              {digit}
            </Button>
          ))}
        </div>
      ))}

      <div className="grid w-full grid-cols-3 gap-3">
        {/*
          The frame draws an empty `spacer` cell 141 × 52 rather than starting the row at `0`, and it is
          given a real height here for the same reason: an empty grid cell collapses, and the design-fidelity
          gate measures boxes.
        */}
        <div className="h-[52px]" />
        <Button fullWidth onClick={() => onDigit('0')} variant="outline">
          0
        </Button>
        {/*
          The glyph is the label the file draws. The accessible name says what it DOES, because "⌫" is
          announced as anything from "erase to the left" to silence depending on the screen reader.
        */}
        <Button fullWidth onClick={onBackspace} variant="outline">
          <span aria-hidden="true">⌫</span>
          <span className="sr-only">Delete the last digit</span>
        </Button>
      </div>
    </div>
  );
}
